import { useEffect, useLayoutEffect, useCallback, useRef, type RefObject } from 'react';
import type { FieldValues } from 'react-hook-form';
import type { FormRef } from '../types';

/** SSR-safe layout effect - uses useEffect on server, useLayoutEffect on client */
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Custom hook for subscribing to form field changes without causing re-renders.
 * Handles conditional Form rendering by re-checking formRef availability on each render.
 *
 * @component
 * @param formRef - Reference to the Form component
 * @param callback - Callback function called when field values change
 * @param deps - Dependency array for the callback memoization
 * @returns void
 *
 * @example
 * ```tsx
 * const formRef = useRef<FormRef>(null);
 *
 * useSubscribe(formRef, (values, { name }) => {
 *   if (name === 'preferredPaymentMethod') {
 *     setPaymentMethod(values.preferredPaymentMethod);
 *   }
 * }, []);
 * ```
 */
export const useSubscribe = (
  formRef: RefObject<FormRef | null>,
  callback: (values: FieldValues, info: { name?: string; type?: string }) => void,
  deps: React.DependencyList = []
): void => {
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
  const subscribedFormRef = useRef<FormRef | null>(null);
  const subscribedCallbackRef = useRef<typeof callback | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedCallback = useCallback(callback, deps);

  /**
   * Synchronous effect to establish/update form subscription.
   * Runs on every render to detect when formRef.current becomes available
   * (refs don't trigger effect re-runs). Uses early returns for performance.
   */
  useIsomorphicLayoutEffect(() => {
    const form = formRef.current;

    // Early exit: Form not available - cleanup any stale subscription
    if (!form) {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
        subscribedFormRef.current = null;
        subscribedCallbackRef.current = null;
      }
      return;
    }

    // Early exit: Already subscribed to same form with same callback
    const isSameForm = subscribedFormRef.current === form;
    const isSameCallback = subscribedCallbackRef.current === memoizedCallback;
    if (subscriptionRef.current && isSameForm && isSameCallback) {
      return;
    }

    // Cleanup previous subscription before creating new one
    subscriptionRef.current?.unsubscribe();

    // Establish new subscription
    subscriptionRef.current = form.subscribe(memoizedCallback);
    subscribedFormRef.current = form;
    subscribedCallbackRef.current = memoizedCallback;
  });

  // Separate unmount cleanup (only runs on component unmount)
  useEffect(() => {
    return () => {
      subscriptionRef.current?.unsubscribe();
      subscriptionRef.current = null;
      subscribedFormRef.current = null;
      subscribedCallbackRef.current = null;
    };
  }, []);
};

export default useSubscribe;
